import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CATS } from "../../data/pusheen";
import { useSound } from "../../sound/SoundContext";
import { burstFromEvent } from "../../lib/burst";

const STAGES = [
  { at: 0, cat: CATS.wave, msg: "Give Pusheen a little boop" },
  { at: 5, cat: CATS.heart, msg: "Pusheen likes you!" },
  { at: 15, cat: CATS.cookie, msg: "Purrfect. Have a cookie" },
  { at: 30, cat: CATS.donut, msg: "Certified chonk enjoyer." },
  { at: 60, cat: CATS.sleep, msg: "Okay okay, Pusheen needs a nap now… zzz" },
];

function stageFor(count) {
  let s = STAGES[0];
  for (const st of STAGES) if (count >= st.at) s = st;
  return s;
}

export default function Interactive() {
  const { play } = useSound();
  const [count, setCount] = useState(0);
  const [pulse, setPulse] = useState(0);
  const best = useRef(0);
  const stage = stageFor(count);

  const pet = (e) => {
    const n = count + 1;
    setCount(n);
    best.current = Math.max(best.current, n);
    setPulse((p) => p + 1);
    play(n % 7 === 0 ? "meow" : "pop");
    burstFromEvent(e, { count: 6, kinds: ["heart", "star"] });
  };

  return (
    <section id="pet" className="relative z-10 py-20 sm:py-28 px-4">
      <div className="max-w-3xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.4 }}
        >
          <p className="font-display font-bold text-pusheen-pink text-lg mb-2">interactive · very serious utility</p>
          <h2 className="font-script text-4xl sm:text-5xl text-pusheen-brown mb-3">Pet the Pusheen</h2>
          <p className="font-body text-pusheen-brownlight text-lg mb-10">The only tokenomics that matter: pets per second.</p>
        </motion.div>

        <div className="bg-white border-[3px] border-pusheen-brown rounded-[2rem] shadow-[6px_6px_0_#4A3728] p-6 sm:p-12">
          <div className="flex items-center justify-center gap-6 sm:gap-10 mb-6">
            <div className="text-center">
              <p className="font-display font-extrabold text-4xl sm:text-5xl text-pusheen-brown leading-none" data-testid="pet-count">{count}</p>
              <p className="font-body text-sm text-pusheen-brownlight mt-1">pets given</p>
            </div>
            <div className="w-px h-12 bg-pusheen-brown/20" />
            <div className="text-center">
              <p className="font-display font-extrabold text-4xl sm:text-5xl text-pusheen-pink leading-none">∞</p>
              <p className="font-body text-sm text-pusheen-brownlight mt-1">love returned</p>
            </div>
          </div>

          <motion.button
            onClick={pet}
            data-testid="pet-button"
            aria-label="Pet Pusheen"
            whileTap={{ scale: 0.9, rotate: -4 }}
            className="relative mx-auto block focus:outline-none"
          >
            <motion.img
              key={pulse}
              src={stage.cat}
              alt="Pusheen"
              initial={{ scale: 1 }}
              animate={{ scale: [1, 1.08, 1] }}
              transition={{ duration: 0.35 }}
              className="w-52 sm:w-60 mx-auto drop-shadow-[6px_8px_0_rgba(74,55,40,0.12)] cursor-pointer select-none"
              draggable="false"
            />
          </motion.button>

          <AnimatePresence mode="wait">
            <motion.p
              key={stage.msg}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              className="font-display font-bold text-pusheen-brown text-xl mt-6"
              data-testid="pet-message"
            >
              {stage.msg}
            </motion.p>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
